// Aggregates "solutions" (closed problems) from three sources so the tools /
// categories browse pages feel populated even before real data lands:
//   1. Real closed problems from the backend (public, artefact redacted)
//   2. Demo mock people's "problems" array (frontend-only mock)
// Each solution is normalised into a common shape used by BrowseTagPage.

import { PEOPLE } from "@/data/mockPeople";

// Convert a mock person's problem into a browsable "solution" summary.
const fromMockPerson = (person) =>
  (person.problems || []).map((p, idx) => ({
    id: `mock-${person.id}-${idx}`,
    problem_id: null, // no detail page for mocks
    title: p.title,
    summary: p.outcome || p.description,
    category: (p.categories || [])[0] || null,
    categories: p.categories || [],
    tools: p.tools || [],
    models: [],
    solved_by_name: person.name,
    solved_by_user_id: null,
    solved_by_picture: person.photo,
    solved_by_slug: person.id,
    solved_at: p.dateSolved,
    isDemo: true,
  }));

// Convert a real closed problem doc into the same shape.
const fromRealClosed = (p) => ({
  id: `real-${p.problem_id}`,
  problem_id: p.problem_id,
  title: p.title,
  summary: (p.solution_artefact || p.description || "").slice(0, 220),
  category: p.category,
  categories: [p.category],
  tools: p.solution_tools || [],
  models: p.solution_models || [],
  solved_by_name: p.solved_by_name,
  solved_by_user_id: p.solved_by_user_id,
  solved_by_picture: "",
  solved_by_slug: null,
  solved_at: p.solved_at || p.created_at,
  isDemo: false,
});

export function buildSolutionsIndex(realClosed = [], demoLimit) {
  const mock = PEOPLE.flatMap(fromMockPerson);
  const real = realClosed.map(fromRealClosed);
  const cappedMock =
    typeof demoLimit === "number" ? mock.slice(0, demoLimit) : mock;
  return [...real, ...cappedMock];
}

// Case-insensitive match on tool OR model name.
export function filterByTool(solutions, tool) {
  const t = tool.trim().toLowerCase();
  return solutions.filter((s) =>
    [...(s.tools || []), ...(s.models || [])]
      .map((x) => x.toLowerCase())
      .includes(t)
  );
}

export function filterByCategory(solutions, category) {
  const c = category.trim().toLowerCase();
  return solutions.filter((s) =>
    (s.categories || []).map((x) => x.toLowerCase()).includes(c)
  );
}

// Return sorted descending by solved_at (most recent first).
export function sortByRecency(list) {
  return [...list].sort((a, b) => {
    const da = a.solved_at ? new Date(a.solved_at).getTime() : 0;
    const db = b.solved_at ? new Date(b.solved_at).getTime() : 0;
    return db - da;
  });
}

// Aggregate counts across the index. Returns { tools: [{name, count}], categories: [{name, count}] }
export function countTagsAcross(solutions) {
  const tools = new Map();
  const cats = new Map();
  for (const s of solutions) {
    for (const t of [...(s.tools || []), ...(s.models || [])]) {
      tools.set(t, (tools.get(t) || 0) + 1);
    }
    for (const c of s.categories || []) {
      cats.set(c, (cats.get(c) || 0) + 1);
    }
  }
  const toArr = (m) =>
    [...m.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return { tools: toArr(tools), categories: toArr(cats) };
}
